/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { type GetBotInfo } from '@adp-common/chat-uikit-shared';
import { MessageBox as UIKitMessageBox } from '@adp-common/chat-uikit';
import { VerboseMsgType } from '@adp-common/chat-core';
import { I18n } from '@adp-arch/i18n';

import { safeJSONParse } from '../../../utils/safe-json-parse';
import { type Message, type MessageGroup } from '../../../store/types';
import { useChatAreaStoreSet } from '../../../hooks/context/use-chat-area-context';
import { usePreference } from '../../../context/preference';

export function getContextProgressText(message: Message): string | undefined {
  if (message.type !== 'verbose') {
    return;
  }
  const content = safeJSONParse(message.content);
  if (
    !content ||
    typeof content !== 'object' ||
    !('msg_type' in content) ||
    content.msg_type !== VerboseMsgType.HARNESS_CONTEXT ||
    !('data' in content) ||
    typeof content.data !== 'string'
  ) {
    return;
  }
  const data = safeJSONParse(content.data);
  return data &&
    typeof data === 'object' &&
    'text' in data &&
    typeof data.text === 'string'
    ? data.text
    : undefined;
}

export function ContextProgressCard({
  records,
  running,
}: {
  records: string[];
  running: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!running) {
      setExpanded(false);
    }
  }, [running]);

  if (!records.length) {
    return null;
  }
  return (
    <div
      className="max-w-full text-xs coz-fg-secondary"
      data-testid="harness-context-progress"
    >
      <button
        type="button"
        className="inline-flex items-center gap-2 w-fit max-w-full rounded-lg coz-bg-secondary px-4 py-3 border-0 text-left text-xs leading-5 coz-fg-secondary cursor-pointer"
        aria-expanded={expanded}
        onClick={() => setExpanded(value => !value)}
      >
        {I18n.t('harness_context_title')}
        <span aria-hidden>{expanded ? '⌃' : '⌄'}</span>
      </button>
      {expanded ? (
        <div className="mt-2 max-h-80 overflow-auto flex flex-col gap-3 rounded-lg coz-bg-secondary p-4">
          {records.map((text, index) => (
            <div key={index} className="whitespace-pre-wrap break-words">
              {text}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function HarnessContextProgress({
  messageGroup,
  getBotInfo,
}: {
  messageGroup: MessageGroup;
  getBotInfo: GetBotInfo;
}) {
  const { layout } = usePreference();
  const { useMessagesStore, useWaitingStore } = useChatAreaStoreSet();
  const records = useMessagesStore(
    useShallow(state =>
      [...messageGroup.memberSet.functionCallMessageIdList]
        .reverse()
        .map(id => state.findMessage(id))
        .filter((message): message is Message => Boolean(message))
        .map(getContextProgressText)
        .filter((text): text is string => Boolean(text)),
    ),
  );
  const running = useWaitingStore(
    state => state.responding?.replyId === messageGroup.groupId,
  );
  if (!records.length) {
    return null;
  }
  return (
    <div className="mt-2 mb-3">
      <UIKitMessageBox
        messageId={null}
        theme="none"
        senderInfo={{ id: '' }}
        showUserInfo={false}
        getBotInfo={getBotInfo}
        layout={layout}
      >
        <ContextProgressCard records={records} running={running} />
      </UIKitMessageBox>
    </div>
  );
}
